import { format, parseISO, differenceInYears, addMonths } from 'date-fns';
import { dateformat } from 'common';
import { isAvMed } from 'context';
import { retrieveBrandConst } from './constants/Constants';
import { DateFormatRegex } from '../../common/utils/constants/Regex';
/**
 * Converts a string representing a date to the MM/DD/YYYY format.
 *
 * @param dateString: The input string representing a date.
 * @returns {string} Returns the formatted date string.
 */
export const convertStringToDate = (dateString: string) => {
  const convertedDate = new Date(dateString);
  return convertedDate.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

/**
 * Formats an ISO date string to 'MM/dd/yyyy' format without considering the timezone.
 *
 * @param dateString - The ISO date string to format. It should be in 'yyyy-MM-ddTHH:mm:ssZ' || 'yyyy-MM-dd' || 'yyyy-MM-ddTHH:mm:ss' format.
 *
 * @returns A string representing the date in 'MM/dd/yyyy' format.
 */
export const formatISODateToMDY = (dateString: string) => {
  try {
    // Split the dateString by 'T' to get the date part
    let newDate = dateString.split('T')[0];

    // Parse the date part into a Date object
    const date = parseISO(newDate);

    // Format the Date object with dateformat.dateFormatMDY as the format string
    return format(date, dateformat.isodateFormatMDY);
  } catch (error) {
    // Handle the error or return a default value as needed
    console.error('Error while formatISODateToMDY:', error);
    return '';
  }
};

/**
 * Converts a date string strictly from YYYY-MM-DD to MM/DD/YYYY format.

 * @param date - The date string in YYYY-MM-DD format.
 * @returns A string in MM/DD/YYYY format, or empty string if invalid.
 */
export const formatYYYYMMDDToMDY = (date: string): string => {
  if (!date) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return '';
  const [, yyyy, mm, dd] = match;
  return `${mm}/${dd}/${yyyy}`;
};

/**
 * Converts a Date object to the MM/DD/YYYY format.
 *
 * @param date: The input Date object.
 * @param isMMDDYYY: Optional parameter to show or hide the month in the formatted date.
 * @returns {string} Returns the formatted date string.
 *
 * mm-dd-yyyy
 */
export const convertToDate = (
  date: Date | string,
  isMMDDYYY: boolean = false
) => {
  const dateObject = typeof date === 'string' ? new Date(date) : date;
  return dateObject.toLocaleDateString(isMMDDYYY ? 'es-CL' : 'en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

/**
 * Converts a string representing a date to the specified date format (default: MM-DD-YYYY).
 *
 * @param date: The input string representing a date.
 */
export const convertAPIRequestFormat = (date: Date | string, dateFormat: string = dateformat.dateFormatHypen) => {
  return format(new Date(date), dateFormat);
};


/**
 * Converts a string representing a date to the specified date format (default: MM/DD/YYYY).
 *
 * @param dateString: The input string representing a date.
 * @param isYYMMDD: Optional parameter to show or hide the year in the formatted date.
 * @returns {string} Returns the formatted date string.
 */

export const convertStrDateFormat = (
  dateString: string,
  isYYMMDD: boolean = false
) => {
  const formattedDate = new Date(dateString).toLocaleDateString(
    isYYMMDD ? 'fr-CA' : 'en-US',
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }
  );

  return formattedDate;
};

/**
 * Converts MM/DD/YYYY UI format to strict YYYY-MM-DD ISO format for API requests.
 * @param dateString Date in MM/DD/YYYY format from the UI
 * @returns ISO date string (YYYY-MM-DD) or undefined if invalid
 */
export const normalizeToISODate = (dateString?: string): string | undefined => {
  if (!dateString) return undefined;
  return convertStrDateFormat(dateString, true);
};

/**
 * Extracts ISO date string (YYYY-MM-DD) from a Date object.
 * @param date Date object
 * @returns ISO date string (YYYY-MM-DD)
 */
export const toISODateString = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Adds months to an ISO date (YYYY-MM-DD) and returns a local YYYY-MM-DD string.
 * @param isoDate ISO date string (YYYY-MM-DD)
 * @param months Number of months to add
 * @returns ISO date string (YYYY-MM-DD) or undefined if input invalid
 */
export const addMonthsToISODate = (
  isoDate?: string,
  months: number = 0
): string | undefined => {
  if (!isoDate) return undefined;
  const parsed = parseISO(isoDate);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const result = addMonths(parsed, months);
  // Build from local parts to avoid UTC offset shifting the day.
  const year = result.getFullYear();
  const month = String(result.getMonth() + 1).padStart(2, '0');
  const day = String(result.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Extracts ISO date (YYYY-MM-DD) from claim API date field (string | Date).
 * Handles both ISO timestamp strings and Date objects from API responses.
 * @param created The created field from claim API response
 * @returns ISO date string (YYYY-MM-DD) or empty string
 */
export const extractISODateFromClaim = (created: string | Date | undefined): string => {
  if (!created) return '';

  if (typeof created === 'string') {
    // API returns ISO format - extract date portion if timestamp
    return created.includes('T') ? created.split('T')[0] : created;
  }
  
  if (created instanceof Date) {
    return toISODateString(created);
  }
  
  return '';
};

/**
 * Returns the last day of the current calendar year as a local YYYY-MM-DD string.
 * @returns ISO date string (YYYY-MM-DD) for December 31 of the current year
 */
export const getCurrentYearEndISODate = (): string => {
  return `${new Date().getFullYear()}-12-31`;
};

const brandConst = retrieveBrandConst(isAvMed());
export const isAgeLessThanRequired = (currentDate: Date): boolean => {
  // Calculate the difference in years
  const diffYears: number = differenceInYears(new Date(), currentDate);
  return diffYears < brandConst.minAge(true);
};

// Set minimum age based on brand requirement
export const avMedPlanYears = () => {
  const pathname = globalThis.location.pathname;
  return pathname.includes('ChildOnlyPlan') ? 0 : 13;
};

export const minYears = isAvMed() ? avMedPlanYears() : 14;

// Set max date a user can input on DatePicker without exceeding the years requirement
export const validateDatePickerInput = (
  value: string,
  minYears: number
): boolean => {
  // Validate format
  if (!DateFormatRegex.test(value)) {
    return false;
  }
  // Parse date
  const inputDate = new Date(value);
  if (Number.isNaN(inputDate.getTime())) {
    return false;
  }
  // Calculate date minAge years ago from today
  const today = new Date();
  const minAge = new Date(
    today.getFullYear() - minYears,
    today.getMonth(),
    today.getDate()
  );
  // Check if input date is on or before the cutoff
  return inputDate <= minAge;
};

// Set max date a user can start picking up date on DatePicker Calendar Dropdown (current-date minus years based on brand requirement)
export const setMaxDate = (minYears: number) => {
  const today = new Date();
  const maxDate = new Date(
    today.getFullYear() - minYears,
    today.getMonth(),
    today.getDate()
  );
  return maxDate.toISOString().split('T')[0];
};

export function getGreeting(t: (key: string) => string): string {
  const hour = new Date().getHours();
  if (hour < 12) return t('medicalClaims.morningText');
  if (hour < 18) return t('medicalClaims.afternoonText');
  return t('medicalClaims.eveningText');
}